import { type Activity, isProjectsOnly, periodLabel } from '@crm/shared';
import { useQuery } from '@tanstack/react-query';
import { type CSSProperties, useEffect } from 'react';
import { Link } from 'react-router';
import { compactSar } from '../../components/AttentionPeek';
import { api, qs } from '../../lib/api';
import { dailyMotivation, type MotivationKind } from '../../lib/dailyMotivation';
import { celebrateOnce } from '../../lib/motion';
import { formatDate, riyadhToday } from '../../lib/format';
import { useSession } from '../../lib/session';
import { Icon } from '../../components/Icon';
import { type NextYearFigures, NextYearStrip } from '../../components/NextYear';

export interface ActivityList { activities: Activity[]; refs: Record<string, string> }

export const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/* The Home target card: this quarter's or this year's target, won, expected finish, months, deals, team. */
/** Home "My goal": this quarter's target, what is won (month by month), what is expected, the best open deals, the team. */
export interface Goal {
  periodType?: 'quarter' | 'year';
  periodStart: string; quota: number | null; closedWon: number; openPipeline: number; openCount?: number; expected?: number; upside?: number; upsideCount?: number; committed?: number; committedCount?: number;
  months?: { start: string; quota: number | null; won: number }[];
  topDeals?: { id: string; name: string; amount: number | null; closeDate: string | null; stage: string | null; probability: number | null; stagePct?: number | null; poConfidence?: number | null }[];
  /** The fewest likely deals that take this person past the target (Negotiate or later first, then the smallest other). */
  tip?: { deals: { id: string; name: string; amount: number }[]; total: number } | null;
  lastWin?: { id: string; name: string; amount: number | null; closeDate: string | null } | null;
  team?: { userId: string; name: string; quota: number; won: number }[];
  /** The year's wins as money (before VAT): invoiced, collected; won deals with no project yet count as won only. */
  cash?: { won: number; invoiced: number; collected: number; fromOdoo: boolean; noProject: number };
  /** The last 3 months: won ÷ what this person's pipeline expected (null without enough nightly copies). */
  forecastHit?: number | null;
  /** From October: next year's pipeline against next year's target. */
  nextYear?: NextYearFigures;
}

/**
 * Top of Home: a greeting with today's work and four figures (open pipeline, expected, won this quarter, tasks), and
 * the quarter's target as a ring (won against the quota) with the days left and the pace.
 */
export function HomeHero({ open, expected, deals, goal, period, onPeriod }: {
  period: 'quarter' | 'year'; onPeriod: (p: 'quarter' | 'year') => void;
  open: number; expected: number; deals: number;
  goal: Goal | undefined;
}) {
  const { user, permissions } = useSession();
  const canAct = permissions.objects.activity.read;
  const canOpp = permissions.objects.opportunity.read;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(today); end.setDate(end.getDate() + 1);
  // The same lists as the Tasks and Today's events cards (shared from the cache).
  const tasks = useQuery({ queryKey: ['my-work', 'tasks', 'today', ymd(today)], enabled: canAct,
    queryFn: () => api<ActivityList>(`/api/activities${qs({ mine: 'true', open: 'true', dueOnOrBefore: ymd(today), limit: 200 })}`) });
  const events = useQuery({ queryKey: ['my-work', 'events', ymd(today)], enabled: canAct,
    queryFn: () => api<ActivityList>(`/api/activities${qs({ mine: 'true', from: today.toISOString(), to: end.toISOString(), limit: 50 })}`) });
  const due = tasks.data?.activities ?? [];
  const overdue = due.filter((t) => t.dueDate && t.dueDate < ymd(today)).length;
  const meetings = (events.data?.activities ?? []).length;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  // The message of the day, for this person's kind of work.
  const kind: MotivationKind = isProjectsOnly(permissions) || (permissions.system.logisticsWork && !permissions.objects.opportunity.read) ? 'projects'
    : permissions.system.presalesWork ? 'presales' : 'sales';
  const motto = dailyMotivation(kind);
  const line = canAct ? [`${due.length - overdue} task${due.length - overdue === 1 ? '' : 's'} due today`, overdue ? `${overdue} overdue` : null,
    `${meetings} event${meetings === 1 ? '' : 's'} today`].filter(Boolean).join(' · ') : null;

  // The quarter: won against the quota (or against everything closing this quarter when there is none).
  const quota = goal?.quota && goal.quota > 0 ? goal.quota : null;
  const base = quota ?? (goal ? goal.closedWon + goal.openPipeline : 0);
  const wonPct = goal && base > 0 ? (goal.closedWon / base) * 100 : 0;
  // Days counted on calendar days in Riyadh (not the browser's clock or time zone).
  const start = new Date(`${goal?.periodStart ?? riyadhToday()}T00:00:00Z`);
  const qEnd = new Date(start); qEnd.setUTCMonth(qEnd.getUTCMonth() + (goal?.periodType === 'year' ? 12 : 3));
  const totalDays = Math.max(1, Math.round((qEnd.getTime() - start.getTime()) / 86400_000));
  const daysLeft = Math.max(0, Math.round((qEnd.getTime() - Date.parse(`${riyadhToday()}T00:00:00Z`)) / 86400_000));
  const elapsed = Math.min(100, Math.max(0, ((totalDays - daysLeft) / totalDays) * 100));
  // Quota reached this quarter: a trophy moment, once per quarter in this browser.
  const reached = !!quota && !!goal && goal.closedWon >= quota;
  useEffect(() => { if (reached && goal) celebrateOnce(`crm.celebrated.quota.${user.id}.${goal.periodStart}`, 'quota'); }, [reached, goal, user.id]);

  return (
    <>
    <div className="hh">
      <section className="hh-card hh-greet">
        <span className="hh-eyebrow">{new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        <h2>{greeting}, {user.name.split(' ')[0]}</h2>
        {line && <p className="hh-line">{line}</p>}
        <p className="hh-motto" title="A new quote every day">“{motto.text}” <span>— {motto.by}</span></p>
        {canOpp && (
          <div className="hh-stats">
            <Link to="/o/opportunity?view=kanban" className="hh-stat"><small>My open pipeline</small><b>{compactSar(open)}</b><small>{deals} deal{deals === 1 ? '' : 's'}</small></Link>
            <div className="hh-stat" title="Each open deal counted by its stage % (Qualified 10%, Quotation sent 50% …)"><small>Expected</small><b>{compactSar(expected)}</b><small>{open > 0 ? `${Math.round((expected / open) * 100)}% of open` : 'nothing open'}</small></div>
            <Link to="/forecasts" className="hh-stat"><small>Won this {goal?.periodType === 'year' ? 'year' : 'quarter'}</small><b>{goal ? compactSar(goal.closedWon) : '…'}</b><small>{goal ? (goal.periodType === 'year' ? goal.periodStart.slice(0, 4) : periodLabel('quarter', goal.periodStart)) : ''}</small></Link>
            {canAct && <Link to="/?tasks=today" className="hh-stat"><small>Tasks today</small><b className={overdue ? 'hh-red' : ''}>{due.length}</b><small>{overdue ? `${overdue} overdue` : 'none overdue'}</small></Link>}
          </div>
        )}
      </section>
    </div>
    {canOpp && goal && <TargetCards goal={goal} quota={quota} wonPct={wonPct} elapsed={elapsed} daysLeft={daysLeft} reached={reached} period={period} onPeriod={onPeriod} />}
    </>
  );
}

/**
 * Home: this quarter's target. Left: won against the target, the expected finish (won + open deals × their win %),
 * the quarter as a timeline, month by month, what a week needs, the open deals' cover and the last win. Right: the
 * open deals most likely to close it, which of them get past the target, and (managers) the team's progress.
 */
function TargetCards({ goal, quota, wonPct, elapsed, daysLeft, reached, period, onPeriod }: { goal: Goal; quota: number | null; wonPct: number; elapsed: number; daysLeft: number; reached: boolean;
  period: 'quarter' | 'year'; onPeriod: (p: 'quarter' | 'year') => void }) {
  const year = goal.periodType === 'year';
  const label = year ? goal.periodStart.slice(0, 4) : periodLabel('quarter', goal.periodStart);
  const { user } = useSession();
  const finish = goal.closedWon + (goal.expected ?? 0);
  const finishPct = quota ? (finish / quota) * 100 : null;
  const pace = !quota ? null : finish >= quota ? 'ahead' : finish >= quota * 0.8 ? 'close' : 'behind';
  const gap = quota ? Math.max(0, quota - goal.closedWon) : 0;
  const weeks = Math.max(1, Math.ceil(daysLeft / 7));
  // What covers the gap: deals at Negotiate or later with a PO expected this quarter (earlier ones are the upside line).
  const committed = goal.committed ?? goal.openPipeline;
  const committedCount = goal.committedCount ?? goal.openCount ?? 0;
  const cover = gap > 0 ? committed / gap : 0;
  const deals = goal.topDeals ?? [];
  // What takes this person past the target: worked out on the server from all open deals of the period.
  const closers = goal.tip?.deals ?? [];
  const sum = goal.tip?.total ?? goal.closedWon;
  const reachable = !!quota && !!goal.tip && closers.length > 0 && goal.closedWon < quota;
  // Whole calendar days between that Riyadh day and today (counting hours rounded a win from this morning to "1 day ago"
  // in the afternoon).
  const daysAgo = (d: string | null) => (d ? Math.max(0, Math.round((Date.parse(`${riyadhToday()}T00:00:00Z`) - Date.parse(`${d.slice(0, 10)}T00:00:00Z`)) / 86400_000)) : null);
  const ago = daysAgo(goal.lastWin?.closeDate ?? null);
  return (
    <div className="tg">
      <section className={`hh-card tg-card${reached ? ' reached' : ''}`}>
        <div className="tg-eye"><span>{quota ? `This ${year ? 'year' : 'quarter'}’s target` : `This ${year ? 'year' : 'quarter'}`} · {label}</span>
          <span className="tg-eye-r">{daysLeft} day{daysLeft === 1 ? '' : 's'} left
            <span className="seg tg-switch" role="group" aria-label="Target period">
              <button type="button" aria-pressed={period === 'year'} onClick={() => onPeriod('year')}>Year</button>
              <button type="button" aria-pressed={period === 'quarter'} onClick={() => onPeriod('quarter')}>Quarter</button>
            </span></span></div>
        <div className="tg-top">
          <span className="tg-ring" style={{ '--p': Math.min(100, wonPct), '--e': Math.min(100, finishPct ?? wonPct) } as CSSProperties} role="img"
            aria-label={`${Math.round(wonPct)}% ${quota ? 'of the target won' : 'won'}`}><span>{Math.round(wonPct)}%<small>{quota ? 'of target' : 'won'}</small></span></span>
          <div>
            <b className="tg-big">{compactSar(goal.closedWon)}</b>
            <p className="tg-line">{quota
              ? <>won of <b>{compactSar(quota)}</b> · {goal.closedWon >= quota ? `${compactSar(goal.closedWon - quota)} above` : `${compactSar(gap)} to go`}</>
              : <>won · no target set for this {goal.periodType === 'year' ? 'year' : 'quarter'}</>}</p>
            {quota && <p className="tg-line" title="What is won, plus each deal at Negotiate or later whose PO is expected this quarter × the lower of its PO confidence and its stage %">Expected finish <b>{compactSar(finish)} ({Math.round(finishPct!)}%)</b>
              <em className={`pace ${pace}`}>{pace === 'ahead' ? 'On track' : pace === 'close' ? 'Slightly behind' : 'Behind'}</em></p>}
            {/* When past forecasts were clearly off, say so next to the expected finish. */}
            {quota && goal.forecastHit != null && (goal.forecastHit < 0.7 || goal.forecastHit > 1.43) && (
              <p className="tg-line tg-hit" title="Won ÷ what your pipeline expected, month by month, over the last 3 months (Forecasts → How good was the forecast?)">
                <Link to="/forecasts">Past forecasts landed at ~{Math.round(goal.forecastHit * 100)}%</Link> of what was expected</p>)}
            {(goal.upsideCount ?? 0) > 0 && <p className="tg-line tg-upside" title="Open deals with a quotation sent, PO expected in this period, not yet at Negotiate: possible, not counted above">
              + {compactSar(goal.upside ?? 0)} possible from {goal.upsideCount} quotation{goal.upsideCount === 1 ? '' : 's'} sent</p>}
          </div>
        </div>
        {/* The quarter as a line: won (solid), expected (dotted), today and the target at the end. */}
        {quota && (
          <div className="tg-pace" aria-hidden="true">
            <span className="track" /><span className="won" style={{ width: `${Math.min(100, wonPct)}%` }} />
            <span className="proj" style={{ left: `${Math.min(100, wonPct)}%`, width: `${Math.max(0, Math.min(100, finishPct ?? 0) - Math.min(100, wonPct))}%` }} />
            <span className="today" style={{ left: `${elapsed}%` }} />
            <span className="lbl" style={{ left: 0 }}>{formatDate(goal.periodStart)}</span><span className="lbl" style={{ left: `calc(${elapsed}% - 14px)` }}>today</span><span className="lbl" style={{ right: 0 }}>target</span>
          </div>
        )}
        {goal.months && goal.months.length > 0 && (
          <div className={`tg-months${year ? ' year' : ''}`}>
            {goal.months.map((m) => {
              const pct = m.quota ? Math.min(100, (m.won / m.quota) * 100) : 0;
              const hit = !!m.quota && m.won >= m.quota;
              return (
                <div key={m.start} className={`tg-m${hit ? ' hit' : ''}`}>
                  <b>{new Date(`${m.start}T00:00:00`).toLocaleDateString(undefined, { month: year ? 'short' : 'long' })}{hit ? ' ✓' : ''}</b>
                  <i><u style={{ width: `${pct}%` }} /></i>
                  <span>{compactSar(m.won)}{m.quota ? ` of ${compactSar(m.quota)}` : ''}</span>
                </div>
              );
            })}
          </div>
        )}
        {year && goal.cash && goal.cash.won > 0 && <CashStrip cash={goal.cash} />}
        {year && goal.nextYear && <NextYearStrip ny={goal.nextYear} />}
        <div className="tg-stats">
          {quota && gap > 0 && <div className="tg-stat">Needed a week<b>{compactSar(gap / weeks)}</b><span>{weeks} week{weeks === 1 ? '' : 's'} left</span></div>}
          <div className={`tg-stat${gap > 0 && cover < 1 ? ' short' : ''}`} title="Open deals at Negotiate or later (stage 50%+) whose PO is expected in this period, full value">Negotiate or later this {year ? 'year' : 'quarter'}<b>{compactSar(committed)}</b>
            <span>{committedCount} deal{committedCount === 1 ? '' : 's'}{gap > 0 ? (cover >= 1 ? ` · covers the gap ${cover.toFixed(1)}×` : ` · find ${compactSar(gap - committed)} more`) : ''}</span></div>
          <div className="tg-stat">Last win<b>{ago == null ? '—' : ago === 0 ? 'Today' : `${ago} day${ago === 1 ? '' : 's'} ago`}</b>
            {goal.lastWin ? <Link to={`/o/opportunity/${goal.lastWin.id}`}>{goal.lastWin.name} · {compactSar(goal.lastWin.amount ?? 0)}</Link> : <span>no win yet</span>}</div>
        </div>
      </section>
      <section className="hh-card tg-card">
        <ForecastGaps />
        <div className="tg-eye"><span>Deals that close the gap</span><span>most likely money first</span></div>
        {deals.length === 0 ? <p className="empty-line small">No open deals closing this quarter.</p> : (
          <div>
            {deals.map((d) => {
              const p = d.probability ?? 0;
              return (
                <Link key={d.id} to={`/o/opportunity/${d.id}`} className="tg-row">
                  <span className="tg-av" aria-hidden="true">{d.name.split(/\s+/).filter((w) => /^[\p{L}]/u.test(w)).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}</span>
                  <span><b>{d.name}</b><small>{[d.stage, d.closeDate ? `PO expected ${formatDate(d.closeDate)}` : null].filter(Boolean).join(' · ')}</small></span>
                  <span className="tg-right"><b>{compactSar(d.amount ?? 0)}</b><span className="tg-chip" title={`The lower of the two · stage ${d.stagePct == null ? '—' : `${Math.round(d.stagePct)}%`} · PO confidence ${d.poConfidence == null ? '—' : `${Math.round(d.poConfidence)}%`}`}>
                    {Math.round(p)}%{d.amount ? ` · likely ${compactSar((d.amount * p) / 100)}` : ' · no value yet'}</span></span>
                </Link>
              );
            })}
          </div>
        )}
        {reachable && (
          <p className="tg-tip"><Icon name="bulb" />Closing {closers.map((d, i) => <span key={d.id}>{i ? (i === closers.length - 1 ? ' and ' : ', ') : ''}<Link to={`/o/opportunity/${d.id}`}><b>{d.name}</b></Link> ({compactSar(d.amount)})</span>)} takes you past the target ({compactSar(sum)}, {Math.round((sum / quota!) * 100)}%).</p>
        )}
        {(goal.team?.length ?? 0) > 1 && (
          <>
            <div className="tg-eye"><span>Team this {year ? 'year' : 'quarter'}</span><span>won of target</span></div>
            <div className="tg-team">
              {goal.team!.map((t) => {
                const pct = t.quota ? (t.won / t.quota) * 100 : 0;
                return (
                  <div key={t.userId} className={`tg-m${pct >= 100 ? ' hit' : ''}`}>
                    <b>{t.userId === user.id ? `${t.name.split(' ')[0]} (you)` : t.name}</b>
                    <i><u style={{ width: `${Math.min(100, pct)}%` }} /></i>
                    <span>{Math.round(pct)}% · {compactSar(t.won)}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * What the forecast can't trust yet: open deals with no amount (counted as 0) and early deals on a 31 Dec placeholder
 * close date. The same items as the attention center below (one request, shared), and "Fix them" opens those rows.
 */
function ForecastGaps() {
  const q = useQuery({ queryKey: ['attention'], queryFn: () => api<{ items: { key: string; text: string; value?: number }[] }>('/api/attention').then((r) => r.items) });
  const pick = (key: string) => q.data?.find((i) => i.key === key);
  const noAmount = pick('no_amount');
  const placeholder = pick('placeholder_close');
  if (!noAmount && !placeholder) return null;
  const n = (it?: { text: string }) => Number(it?.text.split(' ')[0] ?? 0) || 0;
  const open = (key: string) => window.dispatchEvent(new CustomEvent('crm:attention-open', { detail: key }));
  return (
    <div className="tg-gaps" role="note">
      <Icon name="alert" tone="warn" />
      <span>The forecast can’t trust <b>{n(noAmount) + n(placeholder)} open deal{n(noAmount) + n(placeholder) === 1 ? '' : 's'}</b>:
        {noAmount && <> <button type="button" className="link-btn" onClick={() => open('no_amount')}>{n(noAmount)} with no amount</button></>}
        {noAmount && placeholder && ' ·'}
        {placeholder && <> <button type="button" className="link-btn" onClick={() => open('placeholder_close')}>{n(placeholder)} on a 31 Dec placeholder{placeholder.value ? ` (${compactSar(placeholder.value)})` : ''}</button></>}
      </span>
    </div>
  );
}

/**
 * The year's wins as cash: won (signed POs) → invoiced (tax invoices) → collected (money received), all before VAT.
 * A signed PO helps the target; the company needs it invoiced and paid.
 */
function CashStrip({ cash }: { cash: NonNullable<Goal['cash']> }) {
  const pct = (n: number) => (cash.won > 0 ? Math.round((n / cash.won) * 100) : 0);
  const steps = [
    { key: 'is-won', label: 'Won', value: cash.won, pct: 100, to: '/forecasts', title: 'Signed customer POs this year' },
    { key: 'is-invoiced', label: 'Invoiced', value: cash.invoiced, pct: pct(cash.invoiced), to: '/accounts?tab=projects', title: 'Tax invoices made for these wins (Odoo, or recorded in Accounts)' },
    { key: 'is-collected', label: 'Collected', value: cash.collected, pct: pct(cash.collected), to: '/accounts?tab=invoices', title: 'Money received for these wins: Odoo payments, paid milestones, paid PIs' },
  ];
  return (
    <div className="tg-cash" aria-label="This year's wins as cash">
      <div className="tg-eye"><span>This year’s wins as cash</span><span>before VAT</span></div>
      <div className="tg-cash-steps">
        {steps.map((st, i) => (
          <Link key={st.key} to={st.to} className={`tg-cash-step ${st.key}`} title={st.title}>
            <small>{i > 0 && <span aria-hidden="true">→ </span>}{st.label}</small>
            <b>{compactSar(st.value)}</b>
            <i aria-hidden="true"><u style={{ width: `${Math.min(100, st.pct)}%` }} /></i>
            <span>{i === 0 ? 'signed POs' : `${st.pct}% of won`}</span>
          </Link>
        ))}
      </div>
      {(cash.noProject > 0 || cash.fromOdoo) && (
        <p className="tg-cash-note small muted">
          {cash.noProject > 0 && <>{compactSar(cash.noProject)} won has no project yet, so nothing can be invoiced on it. </>}
          {cash.fromOdoo && <>Invoices and payments come partly from the nightly Odoo copy (only what carries the DR-Danat tag).</>}
        </p>
      )}
    </div>
  );
}
